import { Grid, Tabs, Tab, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CardComponent } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import DiscoverTune from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Discover_tune';
import { TabItem } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/CustomiseTabs';
import React, { memo } from 'react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { NoTabsAccess } from '../../molecules/NoTabsAccess';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import { isFeatureAccessible } from '../../../core-utils/helper-utils/userAccess';
import experienceConstants from '../../../core-utils/messages/experienceConstants';

interface TabsSectionProps {
  sortedTabs: TabItem[];
  selectedTabIndex: number;
   
  handleTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  handleCustomiseTabOpen: () => void;
   
  renderTabContent: (tabLabel: string) => React.JSX.Element | null;
  tabsLoading: boolean;
  mainLoader: React.JSX.Element;
  combinedScreens: string[];
  userAccess: any;
}

const useStyles = makeStyles()({
  tabsContainer: {
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    zIndex: 0,
  },
  tab: {
    textTransform: 'none',
    minWidth: 120,
    fontWeight: 500,
    fontSize: '14px',
    zIndex: 10,
    color: '#374151',
    '&.Mui-selected': {
      color: '#4F46E5',
    },
  },
  tabLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '95%',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    zIndex: 0,
  },
  noAccessContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '500px',
  },
});

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  return value === index ? <Box>{children}</Box> : null;
};

const TabsSection = memo(
  ({
    sortedTabs,
    selectedTabIndex,
    handleTabChange,
    handleCustomiseTabOpen,
    renderTabContent,
    tabsLoading,
    mainLoader,
    combinedScreens,
    userAccess,
  }: TabsSectionProps) => {
    const { classes } = useStyles();

    return (
      <CardComponent
        title=""
        width="70%"
        mainGridStyles={{
          padding: '0px 16px',
          height: '614px',
          overflowY: 'auto',
          ...scrollBarStyles,
        }}
        height="614px"
        padding="12px 16px 20px 16px"
        body={
          tabsLoading ? (
            <div
              style={{ display: 'flex', alignItems: 'center', height: '614px' }}
            >
              {mainLoader}
            </div>
          ) : (
            <>
              <Grid
                container
                className={classes.tabsContainer}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Grid className={classes.tabLabelContainer}>
                  <Tabs
                    value={selectedTabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons={false}
                  >
                    {sortedTabs?.map((tab) => (
                      <Tab
                        key={tab.id}
                        label={tab.label}
                        className={classes.tab}
                      />
                    ))}
                  </Tabs>
                </Grid>
                <DiscoverTune
                  style={{ cursor: 'pointer' }}
                  onClick={handleCustomiseTabOpen}
                />
              </Grid>
              {sortedTabs?.map((tab, index) => {
                const accessRoute =
                  experienceConstants.TAB_SCREEN_MAPPING[tab.label];
                return (
                  <TabPanel value={selectedTabIndex} index={index} key={tab.id}>
                    {accessRoute &&
                    isFeatureAccessible(
                      accessRoute,
                      combinedScreens,
                      userAccess,
                    ) ? (
                      renderTabContent(tab?.label)
                    ) : (
                      <Grid className={classes.noAccessContainer}>
                        <NoTabsAccess text="Access denied !" />
                        <Typography
                          fontSize="12px"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                        >
                          You don’t have access to this feature, to know more
                          info visit{' '}
                          <Typography
                            textColor="#475CB0"
                            fontSize={'14px'}
                            lineHeight={'24px'}
                            styleProps={{
                              display: 'inline-block',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dashed',
                              textDecorationThickness: '1px',
                              textDecorationColor: '#475CB0',
                              textUnderlineOffset: '4px',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              window.location.href =
                                'mailto:sales@homahealth.us';
                            }}
                          >
                            sales@homahealth.us.
                          </Typography>
                        </Typography>
                      </Grid>
                    )}
                  </TabPanel>
                );
              })}
            </>
          )
        }
      />
    );
  },
);

export default TabsSection;
